import type { SceneRenderer } from './renderer';
import type { RGBA, Scene, Shape } from '../core/scene';
import { cssColor, WHITE } from '../core/scene';
import { applyPathToCtx } from '../core/geometry';

/**
 * Layered, baked-buffer Canvas2D renderer — the original Alchemy `canvasImage`
 * trick, modernised. Committed shapes live in an offscreen buffer (static, one
 * blit per frame); only the live stroke + its affect output redraw each frame.
 */
export class Canvas2DRenderer implements SceneRenderer {
  private view: HTMLCanvasElement;
  private vctx: CanvasRenderingContext2D;
  private buffer: HTMLCanvasElement;
  private bctx: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private traceImg: HTMLImageElement | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.view = canvas;
    const vctx = canvas.getContext('2d');
    if (!vctx) throw new Error('2D context unavailable');
    this.vctx = vctx;
    this.buffer = document.createElement('canvas');
    const bctx = this.buffer.getContext('2d');
    if (!bctx) throw new Error('2D buffer context unavailable');
    this.bctx = bctx;
  }

  resize(width: number, height: number, dpr: number): void {
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    for (const c of [this.view, this.buffer]) {
      c.width = Math.max(1, Math.round(width * dpr));
      c.height = Math.max(1, Math.round(height * dpr));
    }
    this.view.style.width = `${width}px`;
    this.view.style.height = `${height}px`;
    this.vctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private paintShape(ctx: CanvasRenderingContext2D, s: Shape): void {
    applyPathToCtx(s.path, ctx);
    const st = s.style;
    let paint: string | CanvasGradient = cssColor(st.color);
    if (s.gradient && s.gradient.stops.length > 0) {
      const g = ctx.createLinearGradient(s.gradient.from.x, s.gradient.from.y, s.gradient.to.x, s.gradient.to.y);
      for (const stop of s.gradient.stops) g.addColorStop(Math.max(0, Math.min(1, stop.offset)), cssColor(stop.color));
      paint = g;
    }
    if (st.mode === 'fill') {
      ctx.fillStyle = paint;
      ctx.fill(st.fillRule ?? 'nonzero');
    } else {
      ctx.strokeStyle = paint;
      ctx.lineWidth = st.lineWidth;
      ctx.lineJoin = st.join;
      ctx.lineCap = st.cap;
      ctx.stroke();
    }
  }

  renderBlank(background: RGBA): void {
    const ctx = this.vctx;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = cssColor(background);
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.restore();
  }

  bakeCommitted(shapes: readonly Shape[], background: RGBA): void {
    const ctx = this.bctx;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.fillStyle = cssColor(background);
    ctx.fillRect(0, 0, this.width, this.height);
    if (this.traceImg) {
      const iw = this.traceImg.naturalWidth || this.traceImg.width;
      const ih = this.traceImg.naturalHeight || this.traceImg.height;
      if (iw > 0 && ih > 0) {
        const scale = Math.min(this.width / iw, this.height / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        ctx.globalAlpha = 0.4;
        ctx.drawImage(this.traceImg, (this.width - dw) / 2, (this.height - dh) / 2, dw, dh);
        ctx.globalAlpha = 1;
      }
    }
    for (const s of shapes) this.paintShape(ctx, s);
    ctx.restore();
  }

  setTraceImage(img: HTMLImageElement | null): void {
    this.traceImg = img;
  }

  renderFrame(live: readonly Shape[], guides: readonly Shape[]): void {
    const ctx = this.vctx;
    // Blit committed buffer 1:1 in device pixels (it already encodes dpr).
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.view.width, this.view.height);
    ctx.drawImage(this.buffer, 0, 0);
    ctx.restore();
    // Draw live shapes + guides in logical coordinates.
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    for (const s of live) this.paintShape(ctx, s);
    for (const g of guides) this.paintShape(ctx, g);
    ctx.restore();
  }

  renderSceneToCanvas(scene: Scene, scale: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(scene.width * scale));
    c.height = Math.max(1, Math.round(scene.height * scale));
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('export context unavailable');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = cssColor(scene.background ?? WHITE);
    ctx.fillRect(0, 0, scene.width, scene.height);
    for (const s of scene.shapes) this.paintShape(ctx, s);
    return c;
  }
}
